// source: template-project@7007a338a apps/backoffice/src/components/ui/shadcn/button.tsx
// Ported from the UX lab — `trafiflow_ux_lab/apps/lab/src/components/ui/button.tsx`
// (E5a, audit §V2). This is NOT the shadcn registry button any more: the cva is
// the lab's, so `npx shadcn add button --overwrite` would destroy it.
//
// ── What the port changes ──────────────────────────────────────────────────────
//   • Height ramp: default h-9→h-8, sm h-8→h-7, lg h-10→h-9. Four new sizes
//     (`xs`, `icon-xs`, `icon-lg`; `icon` is now size-8 not h-9 w-9).
//   • Colors: token-based. The six hardcoded `neutral-*` variants are gone.
//     `default` is the lab's primary gradient, not flat `neutral-900`.
//   • Icons: the vendored `[&_svg]:size-4` blanket is replaced by a per-size
//     `[&_svg:not([class*='size-'])]:size-N`. Consequence: an icon that carries
//     its OWN `size-*` class now keeps it — which is the only way one button can
//     hold three differently-sized glyphs (composer: Square 11 / CornerDownLeft
//     12 / ArrowUp 14-15). A button-level `[&_svg]:size-N` no longer wins: the
//     guard's `:not([attr])` outspecifies it, so the few surfaces that relied on
//     that pattern carry `!` until the E5b sweep deletes them.
//   • `loading` prop (Loader2), matching the lab.
//
// ── Deliberate deviations from the lab source (Tailwind 3.4 vs the lab's v4) ───
//   `ring-3`                            → `ring-[3px]`        (same 3px; the
//                                          preset also declares `ringWidth.3`,
//                                          kept as the arbitrary value to stay
//                                          byte-identical to the lab port)
//   `aria-invalid:`                     → `aria-[invalid=true]:` (v3 has no
//                                          built-in aria-invalid variant)
//   `active:not-aria-[haspopup]:`       → `[&:not([aria-haspopup])]:active:`
//   `has-data-[icon=inline-end]:`       → `has-[[data-icon=inline-end]]:`
//   `in-data-[slot=button-group]:`      → `[[data-slot=button-group]_&]:`
//   `[a]:hover:from-primary`/`to-…`     → dropped: v3 rejects an arbitrary
//                                          variant with no `&`, and the plain
//                                          `hover:` pair above it already
//                                          covers the anchor case.
//   `Slot` from `radix-ui`              → `@radix-ui/react-slot` (the package
//                                          this app already depends on).
// Every rewritten candidate above was compiled against this app's Tailwind
// config and verified to emit the same declarations as the lab's.
//
// ── Backoffice-only additions (NOT in the lab) ─────────────────────────────────
//   `brandBlue` variant — TrafiFlow auth CTA, token `--tf-brand-blue`. Keep.
//
// ── Retired in E5b unit 3 ──────────────────────────────────────────────────────
//   The `compactPrimary` variant and the `compact` size are gone, together with
//   `ui/list/ListPageHeader`'s `ACTION_CLASS_NAME` / `SECONDARY_ACTION_CLASS_NAME`
//   and the hand-copy in `subscriptions/list/PlansToolbar`. Audit §V2 calls these
//   three halves of ONE escape hatch (`h-auto … px-4 py-2.5 text-xs …
//   [&_svg]:size-3.5`), so they had to go in a single commit — leaving any one of
//   them keeps the pattern reachable. Do not re-add a size or variant whose job
//   is to escape the ramp; use `size="lg"` if a taller action is genuinely
//   required.
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * Trafiflow UI v3. The variants carry the design system themselves — what the
 * paper theme used to paint over them with `[class~=…]` selectors (§5 of
 * workspace-ds-test.css, now deleted) is written here once:
 *
 *   default      slate primary fill, hover and pressed walk its ramp. Human-
 *                confirmed actions: Approve, Save, Send. No gradient, no shadow.
 *   destructive  the same slate: the theme has no red button, a destructive
 *                action is a primary with explicit copy.
 *   outline      the product default: raised white, hairline, 5% ink on hover,
 *                10% pressed.
 *   secondary    5% ink wash.
 *   secondary-blue  the info badge ground.
 *   ghost / link no box; only the label colour moves (ink → text-2 → pressed).
 *   run          the only Ultra button: what agents run. Only through
 *                `ui/run-button` (RunButton), which always shows the cost;
 *                `ds/run-button-only` fails any other `variant="run"`.
 *   brandBlue    auth pages only.
 *
 * Icon sizes (`icon*`) only size the box and the glyph; the colour is the
 * variant's, like any other size. The square icon button of the document
 * (5% ink with a hairline of the same alpha, 8px radius, 10% on hover, 20%
 * pressed, a ghost tone with no resting box) is `IconButton`
 * (ui/icon-button): new code uses it. The ~290 existing `size="icon*"`
 * Buttons still look like that today because `styles/components/button.css`
 * paints every icon size as the filled square (what the paper theme did),
 * with `data-ghost` as its opt-out; the sweep moves them to IconButton and
 * deletes that sheet.
 * Disabled is a silver fill with the disabled label colour, never opacity.
 * Nothing moves on press (no translate) and focus changes the border, not
 * the box.
 */

const buttonVariants = cva(
  // MODIFIED (develop 679433ebc, re-applied onto the lab cva port): stroke
  // yields to explicit `stroke-*` classes on the icon, so per-icon overrides
  // don't need `!important`.
  "group/button inline-flex shrink-0 items-center justify-center rounded-md border border-transparent bg-clip-padding text-sm font-medium leading-none whitespace-nowrap transition-colors duration-150 outline-none select-none focus-visible:border-ds-focus focus-visible:ring-[3px] focus-visible:ring-ds-focus-halo disabled:pointer-events-none disabled:border-ds-disabled-border disabled:bg-ds-disabled-bg disabled:text-ds-disabled-text aria-[invalid=true]:border-destructive aria-[invalid=true]:ring-[3px] aria-[invalid=true]:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='stroke-'])]:stroke-[1.5] [&_svg:not([class*='size-'])]:size-3",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-ds-primary-hover hover:text-primary-foreground aria-expanded:bg-ds-primary-hover active:bg-ds-primary-pressed",
        outline:
          "border-border bg-ds-raised text-foreground hover:bg-ds-ink-a05 hover:text-foreground aria-expanded:bg-ds-ink-a05 aria-expanded:text-foreground active:bg-ds-ink-a10",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        "secondary-blue":
          "bg-ds-badge-info-bg text-ds-badge-info-text hover:bg-ds-badge-info-bg hover:text-foreground aria-expanded:bg-ds-badge-info-bg aria-expanded:text-foreground",
        ghost:
          "text-foreground hover:bg-transparent hover:text-ds-text-2 aria-expanded:text-foreground active:text-ds-ink-pressed",
        destructive:
          "bg-primary text-primary-foreground hover:bg-ds-primary-hover hover:text-primary-foreground aria-expanded:bg-ds-primary-hover active:bg-ds-primary-pressed",
        link: "text-foreground underline-offset-4 hover:text-ds-text-2 active:text-ds-ink-pressed",
        run: "bg-ds-accent text-ds-accent-ink hover:bg-ds-pixel hover:text-ds-accent-ink active:bg-ds-primary-pressed",
        /**
         * TrafiFlow brand action blue (#001AFF) — auth pages only. Not in the
         * lab; kept because `--tf-brand-blue` is a real product token that
         * `--primary` does not carry (see src/index.css).
         */
        brandBlue: "bg-brand-blue text-brand-blue-foreground hover:bg-brand-blue-hover",
      },
      size: {
        default:
          "h-8 gap-1 px-3 has-[[data-icon=inline-end]]:pr-2.5 has-[[data-icon=inline-start]]:pl-2.5 [&_svg:not([class*='size-'])]:size-3",
        xs: "h-6 gap-1 rounded-md px-2 text-xs [[data-slot=button-group]_&]:rounded-lg has-[[data-icon=inline-end]]:pr-1.5 has-[[data-icon=inline-start]]:pl-1.5 [&_svg:not([class*='size-'])]:size-2.5",
        sm: "h-7 gap-1 rounded-md px-2.5 text-[0.8rem] [[data-slot=button-group]_&]:rounded-lg has-[[data-icon=inline-end]]:pr-1.5 has-[[data-icon=inline-start]]:pl-1.5 [&_svg:not([class*='size-'])]:size-2.5",
        lg: "h-9 gap-1.5 px-3.5 has-[[data-icon=inline-end]]:pr-3 has-[[data-icon=inline-start]]:pl-3 [&_svg:not([class*='size-'])]:size-3.5",
        icon: "size-8 [&_svg:not([class*='size-'])]:size-3.5",
        "icon-xs": "size-6 [[data-slot=button-group]_&]:rounded-lg [&_svg:not([class*='size-'])]:size-2.5",
        "icon-sm": "size-7 [[data-slot=button-group]_&]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        "icon-lg": "size-9 [&_svg:not([class*='size-'])]:size-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

/**
 * @deprecated The `default` variant carries no gradient, shadow or dark border
 * any more, so there is no chrome left to cancel: a `<Button className="bg-…">`
 * gets its own background through tailwind-merge alone. Kept as a no-op for
 * the call sites that still spread it; the sweep removes them. A button that
 * brings its own colour is itself design-system debt (ds/no-palette-classes):
 * pick a variant, or add the intent to the system.
 */
export const BUTTON_OWN_BACKGROUND = "border-transparent"

/** @deprecated See `BUTTON_OWN_BACKGROUND`. */
export const BUTTON_OWN_GRADIENT = "border-transparent"

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /**
   * Show a leading spinner and disable interaction. Ignored when `asChild`
   * (a slotted child must remain a single element).
   */
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant = "default",
    size = "default",
    asChild = false,
    loading = false,
    disabled,
    children,
    ...props
  },
  ref,
) {
  // asChild slots a single element (e.g. an <a>); never inject a spinner there.
  // Forwarding the ref lets Radix triggers (Popover/Tooltip/Dropdown) anchor to it.
  if (asChild) {
    return (
      <Slot
        ref={ref}
        data-slot="button"
        data-variant={variant}
        data-size={size}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      >
        {children}
      </Slot>
    )
  }

  return (
    <button
      ref={ref}
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Loader2 className="animate-spin" /> : null}
      {children}
    </button>
  )
})
Button.displayName = "Button"

export { Button, buttonVariants }
